/**
 * Asking the server for a block of receipt numbers.
 *
 * Specification: ../test/receipt-numbers.spec.ts (through `LeaseSource`).
 * Server side: `apps/api` `/v1/numbering/*`. Master plan §2 D7, §4 M7.
 *
 * THE ONLY ONLINE STEP IN THE NUMBERING PATH, and it happens long before the
 * number is needed: a block of 200 is leased when the till has 20 % left, so
 * the close that actually consumes a number is offline-safe by construction.
 *
 * `Idempotency-Key` ON EVERY LEASE, and it is not decoration. A till asks for
 * 200 numbers, the response is lost to a timeout, and the till retries — which
 * is what an offline-first client is built to do. Without the header the server
 * allocates a second block, the till only ever sees one, and 200 numbers become
 * a gap attributed to a device that never saw them.
 *
 * THE PERIOD IS NOT SENT. It is derived server-side from the venue's business
 * date, because the device's clock and timezone are exactly what cannot be
 * trusted — a sale at 01:00 on 1 January in a 04:00-cutover venue belongs to
 * the previous year's series, and a till that decided that for itself would
 * quietly start a second series.
 */

import type { LeaseSource } from "./receipt-numbers";

export class NumberingFetchError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "NumberingFetchError";
  }
}

export interface NumberingClientArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly deviceId: string;
  readonly fetch?: typeof fetch;
  /** Overridable so a test can pin the key; a uuid otherwise. */
  readonly newIdempotencyKey?: () => string;
}

export class NumberingClient implements LeaseSource {
  readonly #args: NumberingClientArgs;

  constructor(args: NumberingClientArgs) {
    this.#args = args;
  }

  async #post(path: string, body: unknown): Promise<Response> {
    const send = this.#args.fetch ?? fetch;
    const key = this.#args.newIdempotencyKey?.() ?? crypto.randomUUID();

    try {
      return await send(`${this.#args.baseUrl}${path}`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${this.#args.apiKey}`,
          "content-type": "application/json",
          "idempotency-key": key,
        },
        body: JSON.stringify(body),
      });
    } catch (error) {
      throw new NumberingFetchError(`could not reach ${path}`, { cause: error });
    }
  }

  async lease(args: { seriesKey: string; size: number }): Promise<{
    leaseId: string;
    seriesKey: string;
    period: string;
    fromNumber: number;
    toNumber: number;
    expiresAt: Date;
  }> {
    const response = await this.#post("/v1/numbering/lease", {
      venueId: this.#args.venueId,
      deviceId: this.#args.deviceId,
      seriesKey: args.seriesKey,
      size: args.size,
      // No `period`. See the header.
    });

    if (!response.ok) {
      throw new NumberingFetchError(
        `the server refused a numbering lease (${response.status}). A till that cannot lease ` +
          `numbers can still ring and fire, but it cannot close a check.`,
      );
    }

    const body = (await response.json()) as {
      leaseId: string;
      seriesKey: string;
      period: string;
      fromNumber: number;
      toNumber: number;
      expiresAt: string;
    };

    return { ...body, expiresAt: new Date(body.expiresAt) };
  }

  async returnLease(leaseId: string): Promise<void> {
    const response = await this.#post("/v1/numbering/return", {
      venueId: this.#args.venueId,
      leaseId,
    });

    if (!response.ok) {
      throw new NumberingFetchError(`the server refused a lease return (${response.status})`);
    }
  }
}
